/**
 * CampaignList.tsx — Admin broadcast campaign list + detail view (Plan 05-09, D-05-16).
 *
 * Layout (05-UI-SPEC.md Surface 6):
 *   Left panel (campaign list rows):
 *     - Campaign name (from first 30 chars of EN template)
 *     - Status Badge (draft/pending_approval/active/paused+reason/completed/cancelled)
 *     - Recipient count "142 / 600 sent"
 *     - ETA chip (only when active)
 *   Right panel (campaign detail):
 *     - ProgressBar (drip progress, colored by status)
 *     - Auto-pause banner (when paused)
 *     - Cancel confirmation (inline)
 *
 * Status badge variants (05-UI-SPEC.md §Color):
 *   draft            → outline
 *   pending_approval → info
 *   active           → success
 *   paused           → warning
 *   completed        → default
 *   cancelled        → danger
 *
 * Copywriting: 05-UI-SPEC.md §Copywriting Contract.
 */

import * as React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { broadcastsApi, type CampaignDto } from '../../lib/api-client.js';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  ProgressBar,
} from '@crm/ui';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Map campaign status to Badge variant (05-UI-SPEC.md §Surface 6). */
function statusVariant(status: string): React.ComponentProps<typeof Badge>['variant'] {
  switch (status) {
    case 'draft': return 'outline';
    case 'pending_approval': return 'info';
    case 'active': return 'success';
    case 'paused': return 'warning';
    case 'scheduled': return 'info';
    case 'completed': return 'default';
    case 'cancelled': return 'danger';
    default: return 'outline';
  }
}

/** Status label for humans. */
function statusLabel(status: string): string {
  switch (status) {
    case 'draft': return 'Draft';
    case 'pending_approval': return 'Pending approval';
    case 'active': return 'Active';
    case 'paused': return 'Paused';
    case 'scheduled': return 'Scheduled';
    case 'completed': return 'Completed';
    case 'cancelled': return 'Cancelled';
    default: return status;
  }
}

/** Map campaign status to ProgressBar colorVariant (05-UI-SPEC.md §ProgressBar). */
function progressVariant(status: string): React.ComponentProps<typeof ProgressBar>['colorVariant'] {
  switch (status) {
    case 'active': return 'success';
    case 'paused': return 'warning';
    default: return 'muted';
  }
}

/** Format eta seconds into "~Xh Ym remaining". */
function formatEtaRemaining(etaSeconds: number | null | undefined): string {
  if (etaSeconds == null || etaSeconds <= 0) return '';
  const h = Math.floor(etaSeconds / 3600);
  const m = Math.floor((etaSeconds % 3600) / 60);
  if (h > 0) return `~${h}h ${m}m remaining`;
  if (m > 0) return `~${m}m remaining`;
  return `~${etaSeconds}s remaining`;
}

/** Progress percentage (0–100). */
function progressPct(campaign: CampaignDto): number {
  if (!campaign.totalRecipients) return 0;
  return Math.round((campaign.sentCount / campaign.totalRecipients) * 100);
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface CampaignListProps {
  accountId: string;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function CampaignList({ accountId }: CampaignListProps) {
  const queryClient = useQueryClient();
  const [selectedId, setSelectedId] = React.useState<string | null>(null);
  const [confirmCancel, setConfirmCancel] = React.useState(false);

  // ── Data ─────────────────────────────────────────────────────────────────

  const { data: campaigns = [], isLoading, error } = useQuery({
    queryKey: ['broadcasts', accountId],
    queryFn: () => broadcastsApi.list(accountId),
    refetchInterval: 10_000, // poll for drip progress every 10s
  });

  const selected = selectedId
    ? campaigns.find((c) => c.id === selectedId) ?? null
    : null;

  // ── Mutations ─────────────────────────────────────────────────────────────

  const pauseMutation = useMutation({
    mutationFn: (id: string) => broadcastsApi.pause(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['broadcasts', accountId] }),
  });

  const resumeMutation = useMutation({
    mutationFn: (id: string) => broadcastsApi.resume(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['broadcasts', accountId] }),
  });

  const cancelMutation = useMutation({
    mutationFn: (id: string) => broadcastsApi.cancel(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['broadcasts', accountId] });
      setConfirmCancel(false);
    },
  });

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="flex h-full">
      {/* Left panel — campaign list */}
      <div className="w-80 border-r border-border overflow-y-auto flex flex-col">
        <div className="flex-1">
          {isLoading && (
            <p className="text-sm text-text-muted p-4">Loading campaigns…</p>
          )}
          {!isLoading && campaigns.length === 0 && (
            <p className="text-sm text-text-muted text-center py-12 px-4">
              No broadcast campaigns yet. Write a message to start one.
            </p>
          )}
          {!isLoading && error && (
            <p className="text-sm text-danger p-4">
              Could not load campaigns: {(error as Error).message}
            </p>
          )}
          {campaigns.map((campaign) => (
            <button
              key={campaign.id}
              type="button"
              onClick={() => {
                setSelectedId(campaign.id);
                setConfirmCancel(false);
              }}
              className={[
                'w-full text-left px-4 py-3 border-b border-border hover:bg-bg-hover transition-colors',
                selectedId === campaign.id ? 'bg-bg-hover' : '',
              ].join(' ')}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-medium text-text truncate max-w-[160px]">
                  {campaign.name || campaign.messageEn.slice(0, 30)}
                </span>
                <Badge variant={statusVariant(campaign.status)}>
                  {statusLabel(campaign.status)}
                </Badge>
              </div>
              <div className="flex items-center gap-3 mt-1">
                <span className="text-xs text-text-muted">
                  {campaign.sentCount} / {campaign.totalRecipients} sent
                </span>
                {campaign.status === 'active' && campaign.etaSeconds != null && (
                  <span className="text-xs text-info font-mono">
                    {formatEtaRemaining(campaign.etaSeconds)}
                  </span>
                )}
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Right panel — detail view */}
      <div className="flex-1 flex flex-col overflow-y-auto">
        {!selected && (
          <p className="text-sm text-text-muted text-center py-20">
            Select a campaign to view details.
          </p>
        )}

        {selected && (
          <div className="flex flex-col gap-4 p-4">
            {/* Auto-pause banner (05-UI-SPEC.md Surface 6) */}
            {selected.status === 'paused' && (
              <div className="bg-warning/10 border border-warning/30 rounded p-3 flex items-center justify-between gap-4">
                <p className="text-sm text-warning">
                  Campaign paused — account needs attention.
                  Resume when the account is back online.
                </p>
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => setConfirmCancel(true)}
                >
                  Cancel campaign
                </Button>
              </div>
            )}

            {/* Header */}
            <div className="flex items-center justify-between gap-2">
              <h3 className="text-base font-semibold text-text">
                {selected.name || selected.messageEn.slice(0, 30)}
              </h3>
              <Badge variant={statusVariant(selected.status)}>
                {statusLabel(selected.status)}
              </Badge>
            </div>

            {/* Progress bar + stats */}
            <Card>
              <CardContent className="flex flex-col gap-3 pt-4">
                <div className="flex items-center justify-between text-xs font-mono text-text-muted">
                  <span>{selected.sentCount} / {selected.totalRecipients} sent</span>
                  {selected.status === 'active' && selected.etaSeconds != null && (
                    <span className="text-info">{formatEtaRemaining(selected.etaSeconds)}</span>
                  )}
                  <span>{progressPct(selected)}%</span>
                </div>
                <ProgressBar
                  value={progressPct(selected)}
                  colorVariant={progressVariant(selected.status)}
                />
              </CardContent>
            </Card>

            {/* Message preview */}
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Message</CardTitle>
              </CardHeader>
              <CardContent className="flex flex-col gap-3">
                <div>
                  <span className="text-xs text-text-muted uppercase tracking-wide">English</span>
                  <p className="text-sm text-text mt-1 bg-bg-input rounded px-3 py-2">
                    {selected.messageEn}
                  </p>
                </div>
                {selected.messageDe && (
                  <div>
                    <span className="text-xs text-text-muted uppercase tracking-wide">German (OB-1 preview)</span>
                    <p className="text-sm text-text mt-1 bg-bg-input rounded px-3 py-2">
                      {selected.messageDe}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Actions */}
            <div className="flex gap-2 flex-wrap">
              {selected.status === 'active' && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => pauseMutation.mutate(selected.id)}
                  disabled={pauseMutation.isPending}
                >
                  {pauseMutation.isPending ? 'Pausing…' : 'Pause'}
                </Button>
              )}
              {selected.status === 'paused' && (
                <Button
                  variant="success"
                  size="sm"
                  onClick={() => resumeMutation.mutate(selected.id)}
                  disabled={resumeMutation.isPending}
                >
                  {resumeMutation.isPending ? 'Resuming…' : 'Resume'}
                </Button>
              )}
              {!['completed', 'cancelled'].includes(selected.status) && (
                <>
                  {confirmCancel ? (
                    <div className="flex items-center gap-2 p-3 bg-bg-card border border-border rounded">
                      <span className="text-sm text-text">
                        Are you sure you want to cancel this campaign? This cannot be undone.
                      </span>
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => cancelMutation.mutate(selected.id)}
                        disabled={cancelMutation.isPending}
                      >
                        {cancelMutation.isPending ? 'Cancelling…' : 'Cancel campaign'}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setConfirmCancel(false)}
                      >
                        Keep running
                      </Button>
                    </div>
                  ) : (
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => setConfirmCancel(true)}
                    >
                      Cancel campaign
                    </Button>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
